import { useToggle } from '@vueuse/core';
import { onUnmounted, type Ref, ref } from 'vue';

export interface UseToggleAnimationOptions {
  /**
   * Duration of the animation in milliseconds
   * @default 300
   */
  duration?: number;
  /**
   * Whether to start with the element visible
   * @default false
   */
  initialVisible?: boolean;
}

export interface UseToggleAnimationReturn {
  /**
   * Whether the element should be visible in the DOM
   */
  isVisible: Ref<boolean>;
  /**
   * Whether the element should show the "show" animation (fade-in)
   */
  shouldShow: Ref<boolean>;
  /**
   * Whether the element should show the "hide" animation (fade-out)
   */
  shouldHide: Ref<boolean>;
  /**
   * Function to toggle the visibility with animation
   */
  toggle: (visible: boolean) => void;
  /**
   * Function to show the element with animation
   */
  show: () => void;
  /**
   * Function to hide the element with animation
   */
  hide: () => void;
  /**
   * Function to immediately set visibility without animation
   */
  setVisible: (visible: boolean) => void;
  /**
   * Cleanup function to clear any pending timeouts
   */
  cleanup: () => void;
}

/**
 * Composable for managing toggle animations with proper timeout cleanup
 *
 * @param options Configuration options for the animation
 * @returns Object with visibility state and control functions
 *
 */
export function useToggleAnimation(options: UseToggleAnimationOptions = {}): UseToggleAnimationReturn {
  const { duration = 300, initialVisible = false } = options;

  const [isVisible, toggleIsVisible] = useToggle(initialVisible);
  const [shouldShow, toggleShouldShow] = useToggle(initialVisible);
  const [shouldHide, toggleShouldHide] = useToggle(false);
  const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Clear any existing timeout to prevent race conditions
   */
  function clearExistingTimeout(): void {
    if (timeoutId.value !== null) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
  }

  /**
   * Set visibility immediately without animation
   */
  function setVisible(visible: boolean): void {
    clearExistingTimeout();
    if (isVisible.value !== visible) {
      toggleIsVisible();
    }
    if (shouldShow.value !== visible) {
      toggleShouldShow();
    }
    if (shouldHide.value) {
      toggleShouldHide();
    }
  }

  /**
   * Toggle visibility with animation
   */
  function toggle(visible: boolean): void {
    clearExistingTimeout();

    if (visible) {
      // Show with fade-in
      if (!isVisible.value) {
        toggleIsVisible();
      }
      if (!shouldShow.value) {
        toggleShouldShow();
      }
      if (shouldHide.value) {
        toggleShouldHide();
      }
    } else {
      // Hide with fade-out, then remove from DOM after animation
      if (shouldShow.value) {
        toggleShouldShow();
      }
      if (!shouldHide.value) {
        toggleShouldHide();
      }

      timeoutId.value = setTimeout(() => {
        if (isVisible.value) {
          toggleIsVisible();
        }
        if (shouldHide.value) {
          toggleShouldHide();
        }
        timeoutId.value = null;
      }, duration);
    }
  }

  /**
   * Show the element with animation
   */
  function show(): void {
    toggle(true);
  }

  /**
   * Hide the element with animation
   */
  function hide(): void {
    toggle(false);
  }

  /**
   * Cleanup function to clear any pending timeouts
   */
  function cleanup(): void {
    clearExistingTimeout();
  }

  // Cleanup timeout on component unmount to prevent memory leaks
  onUnmounted(() => {
    cleanup();
  });

  return {
    isVisible,
    shouldShow,
    shouldHide,
    toggle,
    show,
    hide,
    setVisible,
    cleanup,
  };
}
